import * as Yup from "yup";
import * as uuid from "uuid";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useState } from "react";
import { Auth } from "aws-amplify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled, useTheme } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import AppLink from "../../../../components/elements/AppLink";
import FormFieldError from "../../../../components/elements/FormFieldError";
import PasswordField from "../../../../components/redesign/elements/PasswordField";
import SocialAuthButtons from "../../../../components/modules/SocialAuthButtons";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { createAgency } from "../../../../redux/reducers/agency";
import { getCognitoPasswordSchema, passwordsMatchSchema } from "../../../../utils/yupValidationUtils";
import { setIsAuthLoading } from "../../../../redux/reducers/session";
import { successToaster } from "../../../../helpers/toasters";

const FormColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const RedesignedTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: `1px solid ${theme.palette.brand.gray200}`,
      borderRadius: 8,
    },
    "&.Mui-focused fieldset": {
      border: `1px solid ${theme.palette.brand.blueAccent}`,
    },
  },
}));

const CoralButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  borderRadius: 8,
  fontSize: 16,
  fontWeight: 600,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.brand.coralHover,
  },
  "&:disabled": {
    backgroundColor: theme.palette.brand.coral,
    opacity: 0.7,
  },
}));

type FormInitialValues = {
  userName: string;
  companyName: string;
  email: string;
  password: string;
  passwordConfirm: string;
};

const validationSchema = Yup.object({
  userName: Yup.string().required("Required field."),
  companyName: Yup.string().required("Required field."),
  email: Yup.string().email("Wrong format, enter valid email").required("Required field."),
  password: getCognitoPasswordSchema(),
  passwordConfirm: passwordsMatchSchema("password"),
});

const RedesignedSignUpForm: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme<Theme>();
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const isAuthLoading = useSelector((state: ReduxApplicationState) => state.session.isLoading);

  const formInitialValues: FormInitialValues = {
    userName: "",
    companyName: "",
    email: "",
    password: "",
    passwordConfirm: "",
  };

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      const { userName, password, email, companyName } = values;
      const lowerCaseUserName = userName.toLowerCase();

      const agencyToCreate = {
        agencyId: `bgAgency-${uuid.v4()}`,
        createdBy: lowerCaseUserName,
        email,
        name: companyName,
        users: [lowerCaseUserName],
      };

      dispatch(setIsAuthLoading(true));
      await Auth.signUp({ username: lowerCaseUserName, password, attributes: { email } });
      dispatch(createAgency(agencyToCreate));
      dispatch(setIsAuthLoading(false));
      actions.resetForm();
      successToaster("Confirmation code was successfully sent to your email!");
      history.push({ search: new URLSearchParams({ userName: lowerCaseUserName }).toString() });
    } catch (error) {
      const err = error as any;
      dispatch(setIsAuthLoading(false));
      setAuthErrorMessage(`There was an error signing up: ${err.message}`);
    }
  };

  const clearAuthError = () => {
    if (authErrorMessage) {
      setAuthErrorMessage("");
    }
  };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur
      validateOnChange
    >
      {({ errors, touched, handleChange, handleBlur, values, submitCount }) => {
        const showError = (field: keyof FormInitialValues) =>
          Boolean((touched[field] || submitCount > 0) && errors[field]);

        const handleFieldChange = (field: keyof FormInitialValues) => (e: ChangeEvent<any>) => {
          clearAuthError();
          handleChange(field)(e);
        };

        return (
          <Form>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography style={{ fontSize: 24, fontWeight: 700, color: theme.palette.brand.gray900 }}>
                Create Account
              </Typography>
              <AccountCircleIcon style={{ fontSize: 40, color: theme.palette.brand.gray900 }} />
            </Box>

            <FormColumn>
              <FieldGroup>
                <FieldLabel>Username</FieldLabel>
                <Field
                  as={RedesignedTextField}
                  value={values.userName}
                  error={showError("userName") || !!authErrorMessage}
                  name="userName"
                  variant="outlined"
                  fullWidth
                  onChange={handleFieldChange("userName")}
                  onBlur={handleBlur}
                />
                <FormFieldError name="userName" />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Email</FieldLabel>
                <Field
                  as={RedesignedTextField}
                  value={values.email}
                  error={showError("email") || !!authErrorMessage}
                  name="email"
                  variant="outlined"
                  fullWidth
                  onChange={handleFieldChange("email")}
                  onBlur={handleBlur}
                />
                <FormFieldError name="email" />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Company/Agency Name</FieldLabel>
                <Field
                  as={RedesignedTextField}
                  value={values.companyName}
                  error={showError("companyName") || !!authErrorMessage}
                  name="companyName"
                  variant="outlined"
                  fullWidth
                  onChange={handleFieldChange("companyName")}
                  onBlur={handleBlur}
                />
                <FormFieldError name="companyName" />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Password</FieldLabel>
                <PasswordField
                  value={values.password}
                  error={showError("password") || !!authErrorMessage}
                  name="password"
                  label=""
                  onChange={handleFieldChange("password")}
                  onBlur={handleBlur}
                />
                <Typography style={{ fontSize: 12, color: theme.palette.brand.gray500 }}>
                  Min. 8 characters with uppercase, lowercase, number, and symbol
                </Typography>
                <FormFieldError name="password" />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Confirm Password</FieldLabel>
                <PasswordField
                  value={values.passwordConfirm}
                  error={showError("passwordConfirm") || !!authErrorMessage}
                  name="passwordConfirm"
                  label=""
                  onChange={handleFieldChange("passwordConfirm")}
                  onBlur={handleBlur}
                />
                <FormFieldError name="passwordConfirm" />
                {authErrorMessage && (
                  <Typography style={{ fontSize: 13, color: theme.palette.brand.errorRed }}>
                    {authErrorMessage}
                  </Typography>
                )}
              </FieldGroup>
            </FormColumn>

            <CoralButton
              variant="contained"
              type="submit"
              fullWidth
              disabled={isAuthLoading}
              disableElevation
              style={{ marginTop: 24 }}
            >
              {isAuthLoading ? (
                <CircularProgress size={22} style={{ color: theme.palette.brand.white }} />
              ) : (
                "Create Account"
              )}
            </CoralButton>

            <SocialAuthButtons onError={setAuthErrorMessage} />

            <Box display="flex" justifyContent="center" mt={2}>
              <span>
                <AppLink to="/">Already have an account?</AppLink>{" "}
                <AppLink to="/" highlighted>
                  Log in
                </AppLink>
              </span>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RedesignedSignUpForm;
